import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';

import type { MapEditorCatalogElement } from '../mapEditorCatalog';
import {
  chainGlyph,
  interiorDrawing,
  moduleGlyph,
  type MapEditorInterior,
} from '../mapEditorPlan';
import { spotCountOf } from '../mapEditorRoots';
import { usePlanGlyphStyles } from './styles';

// The map editor's pictures: one module in plan, a whole chain in plan, and the inside of a module.
//
// Where the kit ships a render, every surface that shows a module shows it — the picker's root list,
// its variant tiles and the large preview alike. A build captures only the levels an artist has
// saved from a viewport, so a kit is normally part rendered and part not, and the drawings below are
// what the rest fall back to. They are built from the build's own measurements, so they are wrong
// about the art and right about the shape, the length and the number of walls with something on
// them, which is what the picker is asked to tell apart.
//
// The chain glyph has no render to prefer: it draws a whole walk, which is a thing the author is
// assembling rather than a module the build could have captured.

/**
 * The render this module ships, while it is loadable.
 *
 * `element.thumbnailUrl` is a URL this hub can request, or empty — `useMapEditorCatalog` resolves
 * it against the renders the upload published for the streamed build and clears anything it could
 * not resolve, so nothing here has to know where a build's `WebEditor/` is hosted. That mattered:
 * for a release this component put the catalog's *relative* path into the `src`, the browser
 * resolved it against the current SPA route, and the SPA fallback answered HTTP 200 with a page of
 * HTML — a request that "succeeded" and then failed to decode, which from the hub looked exactly
 * like a build that had shipped no renders (MV-1953).
 *
 * `onError` stays, because a resolved URL can still fail: an object deleted from the assets bucket,
 * or a CloudFront error. Resets on a new `src` so a module whose picture failed does not poison the
 * next one rendered through the same element slot.
 *
 * @param element - The module.
 * @param preferFullSize - Whether this surface fills a frame with the picture, and so wants the
 *        full-size render before the tile.
 * @returns `src` — empty whenever the drawing should be shown instead — and the `onError` that
 *          steps down to the next candidate.
 */
const useModuleImage = (
  element: MapEditorCatalogElement,
  preferFullSize = false,
) => {
  // Only the surfaces that fill a frame ask for the full-size render; a 48x36 tile would pay
  // 1280x720 for a picture nobody can see the detail in.
  const fullSize = preferFullSize ? element.previewUrl : '';
  const tile = element.thumbnailUrl;

  const [hasFullSizeFailed, setHasFullSizeFailed] = useState(false);
  const [hasTileFailed, setHasTileFailed] = useState(false);

  useEffect(() => {
    setHasFullSizeFailed(false);
    setHasTileFailed(false);
  }, [fullSize, tile]);

  // The tile is the same capture downscaled, so it is the right thing to try when the full-size
  // render is named but does not load — a build that published one usually published both, and
  // showing the drawing instead would claim the build shipped no picture of this module when it
  // did.
  const src =
    (!hasFullSizeFailed && fullSize) || (!hasTileFailed && tile) || '';

  const onError = useCallback(() => {
    if (src && src === fullSize) {
      setHasFullSizeFailed(true);

      return;
    }

    setHasTileFailed(true);
  }, [src, fullSize]);

  return { src, onError };
};

/**
 * The corner marker that says a slot is showing a stand-in rather than the build's own render.
 *
 * Drawn wherever a picture cannot be shown, which is two different states an author has no other way
 * to tell apart: a module the build never captured — normal, and most of a kit — and one whose render
 * is named but did not load, which is a fault somebody has to fix. Both are answered here the same
 * way on purpose: what the author can act on is "there is no photo of this", and which of the two
 * caused it is the build's problem, not theirs.
 *
 * @param props.compact - Icon alone, for the 48x36 and 84x40 tiles where a caption cannot be read.
 */
const MissingRenderMark = ({ compact = false }: { compact?: boolean }) => {
  const { t: translate } = useTranslation();
  const styles = usePlanGlyphStyles();

  const label = translate(
    'editorMode.mapEditorNoRenderYet',
    'No picture from the build yet',
  );

  if (compact) {
    return (
      <Box sx={styles.missingRenderBadge} title={label}>
        <ImageNotSupportedOutlinedIcon sx={styles.missingRenderBadgeIcon} />
      </Box>
    );
  }

  return (
    <Box sx={styles.missingRenderNote}>
      <ImageNotSupportedOutlinedIcon sx={styles.missingRenderBadgeIcon} />
      {label}
    </Box>
  );
};

interface IModulePlanGlyphProps {
  element: MapEditorCatalogElement;
  width: number;
  height: number;
}

/**
 * One module at picker size: the render the build shipped, or its footprint drawn in plan.
 *
 * The two answer different questions, and which one an author gets is the kit's to decide rather
 * than this component's. The render tells `Lit Corridor` from `Screen Corridor`, which the footprint
 * cannot — they have the same one. The footprint tells `Short · right` from `Short · left`, which a
 * render taken from the entry doorway does not show either. Where a render exists it is shown, and
 * the label beside every tile carries the turn in words for the case that costs.
 *
 * @param props.element - The module.
 * @param props.width - Rendered width in px.
 * @param props.height - Rendered height in px.
 */
export const ModulePlanGlyph = ({
  element,
  width,
  height,
}: IModulePlanGlyphProps) => {
  const glyph = moduleGlyph(element.footprint, element.direction);
  const styles = usePlanGlyphStyles();
  const thumbnail = useModuleImage(element);

  if (thumbnail.src) {
    return (
      <Box
        component="img"
        src={thumbnail.src}
        onError={thumbnail.onError}
        alt=""
        sx={{
          width,
          height,
          display: 'block',
          objectFit: 'cover',
          borderRadius: '4px',
        }}
      />
    );
  }

  return (
    <Box sx={styles.missingRenderSlot}>
      <svg
        width={width}
        height={height}
        viewBox={glyph.viewBox}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden
      >
        <rect
          x={glyph.box.x}
          y={glyph.box.y}
          width={glyph.box.width}
          height={glyph.box.height}
          fill="rgba(174, 182, 255, 0.16)"
          stroke="#AEB6FF"
          strokeWidth={glyph.strokeWidth}
          rx={glyph.radius}
        />
        <polyline
          points={glyph.walk}
          fill="none"
          stroke="rgba(255, 255, 255, 0.55)"
          strokeWidth={glyph.walkStrokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <MissingRenderMark compact />
    </Box>
  );
};

interface IChainPlanGlyphProps {
  elements: MapEditorCatalogElement[];
  width: number;
  height: number;
}

/**
 * A whole chain in plan: the shape a starter layout would put on the ground.
 *
 * @param props.elements - The chain, in order.
 * @param props.width - Rendered width in px.
 * @param props.height - Rendered height in px.
 * @returns The drawing, or null for an empty chain — which has no shape to draw.
 */
export const ChainPlanGlyph = ({
  elements,
  width,
  height,
}: IChainPlanGlyphProps) => {
  const glyph = chainGlyph(elements);
  if (!glyph) {
    return null;
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={glyph.viewBox}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      {glyph.boxes.map((box, index) => (
        <rect
          // Index-keyed on purpose: these are positions in one drawing rather than entities, and two
          // placements of the same module are legitimately identical here.
          key={`${box.x},${box.y},${index}`}
          x={box.x}
          y={box.y}
          width={box.width}
          height={box.height}
          fill="rgba(174, 182, 255, 0.18)"
          stroke="#AEB6FF"
          strokeWidth={glyph.strokeWidth}
        />
      ))}
    </svg>
  );
};

// What the interior drawing paints a surface with, when the author has put a finish on it. Keyed by
// semantic slot, so a wall re-dressed in the panel is a wall that changes colour in the picture —
// which is the only feedback there is until the real render arrives.
export interface MapEditorSurfaceTints {
  wall?: string;
  floor?: string;
  ceiling?: string;
}

const DEFAULT_TINTS: Required<MapEditorSurfaceTints> = {
  wall: 'rgba(255, 255, 255, 0.05)',
  floor: 'rgba(255, 255, 255, 0.04)',
  ceiling: 'rgba(255, 255, 255, 0.022)',
};

interface IRoomPictureProps {
  element: MapEditorCatalogElement;
  // Finishes to paint the surfaces with. Absent surfaces keep the undressed shade.
  tints?: MapEditorSurfaceTints;
  // Whether to say, on the frame, that the picture is a drawing. On in the picker, where the author
  // is choosing from pictures and has to know what they are looking at; off on a segment that is
  // standing, where the panel has said it once already.
  showDrawingNote?: boolean;
}

/**
 * The picture of a module: the render the build shipped, or the drawn interior when it shipped none.
 *
 * @param props.element - The module.
 * @param props.tints - Per-surface colours for the drawing.
 * @param props.showDrawingNote - Whether to caption the drawing as a drawing.
 */
export const RoomPicture = ({
  element,
  tints,
  showDrawingNote = false,
}: IRoomPictureProps) => {
  const { t: translate } = useTranslation();
  const styles = usePlanGlyphStyles();
  const thumbnail = useModuleImage(element, true);

  // The one field that makes the drawing unnecessary — for the modules that have it. A build
  // captures only the levels an artist has saved from a viewport, so the rest fall through.
  if (thumbnail.src) {
    return (
      <Box sx={styles.picture}>
        <Box
          component="img"
          src={thumbnail.src}
          onError={thumbnail.onError}
          alt={element.displayName}
          sx={styles.pictureImage}
        />
      </Box>
    );
  }

  const spots = spotCountOf(element);
  // The count and the shapes come from two different fields, and both are optional independently —
  // so the count still drives how many frames are drawn, and the sizes only decide what shape each
  // one is. A build answering one and not the other draws correctly either way.
  const interior: MapEditorInterior = interiorDrawing(
    spots.total,
    element.spots?.surfaces ?? [],
  );
  const surface = { ...DEFAULT_TINTS, ...(tints ?? {}) };

  return (
    <Box sx={styles.picture}>
      <svg
        width="100%"
        height="100%"
        viewBox={interior.viewBox}
        preserveAspectRatio="xMidYMid slice"
        aria-hidden
      >
        <rect width="100%" height="100%" fill="#111116" />
        <polygon points={interior.wallLeft} fill={surface.wall} />
        <polygon points={interior.wallRight} fill={surface.wall} />
        <polygon points={interior.floor} fill={surface.floor} />
        <polygon points={interior.ceiling} fill={surface.ceiling} />
        {interior.lines.map((line) => (
          <line
            key={`${line.x1},${line.y1},${line.x2},${line.y2}`}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke="rgba(255, 255, 255, 0.12)"
            strokeWidth={1}
          />
        ))}
        {interior.panels.map((points) => (
          <polygon
            key={points}
            points={points}
            fill="rgba(174, 182, 255, 0.20)"
            stroke="rgba(174, 182, 255, 0.55)"
            strokeWidth={1.2}
          />
        ))}
      </svg>
      {showDrawingNote && (
        <Box sx={styles.pictureNote}>
          {translate(
            'editorMode.mapEditorPictureIsDrawing',
            'Drawing — the real picture comes from the build',
          )}
        </Box>
      )}
      {/* Unconditional, unlike the caption beside it. The caption explains what the author is looking
          at and is only worth the room in the picker; this says the picture is missing, which is
          just as true on the segment panel — and that panel was the one surface where nothing said
          it at all. */}
      <MissingRenderMark />
    </Box>
  );
};
